import React, { useState } from 'react';
import { demoWorkflow, sampleProjects } from '../../data/demoData';
import WorkflowStep from './WorkflowStep';
import StateSimulator from './StateSimulator';
import CameraFrame from '../../components/ui/CameraFrame';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTerminal, FiLayers, FiDatabase, FiExternalLink } = FiIcons;

const DemoView = () => {
  const [activeStepId, setActiveStepId] = useState('setup');
  const activeStep = demoWorkflow.find(s => s.id === activeStepId);

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in duration-700">
      {/* Header */}
      <header className="border-b border-[#222] pb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-pink-500/10 border border-pink-500/30 rounded-sm">
            <SafeIcon icon={FiTerminal} className="text-pink-500 w-5 h-5" />
          </div>
          <h1 className="text-4xl font-black text-white tracking-tighter uppercase">Demo Environment</h1>
        </div>
        <p className="font-mono text-sm text-[#888] max-w-2xl leading-relaxed">
          Welcome to the PhotoPilot sandbox. Explore the end-to-end creator workflow, 
          simulate system edge-cases, and audit sample production data.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Workflow Explorer */}
        <div className="lg:col-span-4 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-mono text-xs text-[#666] tracking-[0.3em] uppercase">Try this flow</h3>
            <span className="text-[10px] text-pink-500/70 font-mono">STEP-BY-STEP</span>
          </div>
          <div className="space-y-3">
            {demoWorkflow.map((step) => (
              <WorkflowStep 
                key={step.id} 
                step={step} 
                active={activeStepId === step.id}
                onClick={() => setActiveStepId(step.id)}
              />
            ))}
          </div>
        </div>

        {/* Right Column: Dynamic Preview */}
        <div className="lg:col-span-8 space-y-8">
          
          {/* Active Flow Preview */}
          <div className="space-y-4">
            <h3 className="font-mono text-xs text-[#666] tracking-[0.3em] uppercase flex items-center gap-2">
              <SafeIcon icon={FiLayers} /> Workflow Detail
            </h3>
            <CameraFrame className="p-8 bg-gradient-to-br from-[#121212] to-[#0a0a0a]">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-1 space-y-6">
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-2">{activeStep.title}</h2>
                    <p className="text-[#aaa] leading-relaxed">{activeStep.description}</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <div className="px-4 py-2 border border-[#333] bg-[#1a1a1a] font-mono text-[10px] text-pink-500 uppercase tracking-widest">
                      Module: {activeStep.module}
                    </div>
                    <button className="px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white font-mono text-[10px] uppercase tracking-widest transition-all">
                      {activeStep.action} →
                    </button>
                  </div>
                </div>
                <div className="w-full md:w-64 aspect-square bg-[#0a0a0a] border border-[#222] rounded-sm flex items-center justify-center relative overflow-hidden group">
                   <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1542038784456-1ea8e935640e?q=80&w=400&auto=format&fit=crop')] bg-cover bg-center grayscale group-hover:scale-110 transition-transform duration-1000"></div>
                   <div className="relative z-10 text-center p-4">
                      <div className="w-12 h-12 border border-pink-500 mx-auto mb-3 flex items-center justify-center">
                        <SafeIcon icon={FiExternalLink} className="text-pink-500" />
                      </div>
                      <span className="font-mono text-[10px] text-white tracking-widest uppercase">Launch Workspace</span>
                   </div>
                </div>
              </div>
            </CameraFrame>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Simulated States */}
            <div className="space-y-4">
              <h3 className="font-mono text-xs text-[#666] tracking-[0.3em] uppercase flex items-center gap-2">
                <SafeIcon icon={FiTerminal} /> Simulated States
              </h3>
              <StateSimulator />
            </div>

            {/* Sample Data */}
            <div className="space-y-4">
              <h3 className="font-mono text-xs text-[#666] tracking-[0.3em] uppercase flex items-center gap-2">
                <SafeIcon icon={FiDatabase} /> Sample Data
              </h3>
              <CameraFrame className="bg-[#111] overflow-hidden">
                <div className="p-4 border-b border-[#222] bg-[#1a1a1a] flex justify-between">
                   <span className="font-mono text-[10px] text-[#888]">PROJECT REGISTRY</span>
                   <span className="font-mono text-[10px] text-pink-500">3 ENTRIES</span>
                </div>
                <div className="divide-y divide-[#222]">
                  {sampleProjects.map(proj => (
                    <div key={proj.id} className="p-4 flex justify-between items-center hover:bg-white/5 transition-colors group cursor-pointer">
                      <div>
                        <p className="text-sm font-bold text-white group-hover:text-pink-500 transition-colors">{proj.name}</p>
                        <p className="font-mono text-[9px] text-[#555] uppercase tracking-tighter">{proj.client} • {proj.id}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-[10px] text-[#aaa]">{proj.progress}%</p>
                        <div className="w-16 h-1 bg-[#222] mt-1">
                          <div className="h-full bg-pink-500" style={{ width: `${proj.progress}%` }}></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CameraFrame>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Credit */}
      <footer className="pt-12 border-t border-[#222] text-center">
         <p className="font-mono text-[#444] text-[10px] tracking-[0.5em] uppercase mb-2">Authenticated Demo Session</p>
         <p className="font-mono text-[#888] text-sm tracking-widest">
           Made by <span className="text-pink-500/80">LingLink AI</span> and <span className="text-pink-500/80">DigLab AI</span>
         </p>
      </footer>
    </div>
  );
};

export default DemoView;